<template>
    <div>
        <!-- Error state -->
        <div v-if="errorState" class="pi-error">
            <div class="pi-error__card">
                <div class="pi-error__icon">
                    <font-awesome-icon v-if="errorState === 'inactive'" icon="fa-solid fa-lock" />
                    <font-awesome-icon v-else icon="fa-solid fa-circle-xmark" />
                </div>
                <h1 class="pi-error__title" v-if="errorState === 'inactive'">Invitatie indisponibila</h1>
                <h1 class="pi-error__title" v-else>Invitatie negasita</h1>
                <p class="pi-error__desc" v-if="errorState === 'inactive'">
                    Aceasta invitatie nu este inca activa. Proprietarul trebuie sa activeze proiectul pentru ca invitatia sa fie accesibila public.
                </p>
                <p class="pi-error__desc" v-else>
                    {{ errorMessage || 'Invitatia pe care incerci sa o accesezi nu a fost gasita sau nu mai este disponibila.' }}
                </p>
                <router-link to="/" class="pi-error__btn">
                    <font-awesome-icon icon="fa-solid fa-arrow-left" />
                    <span>Inapoi la pagina principala</span>
                </router-link>
            </div>
        </div>

        <div v-else ref="projectsContainer" class="projects overflow-x-hidden">
            <!-- Loading overlay — visible until data + fonts are ready -->
            <transition name="pi-loader-fade">
                <div v-if="!contentReady" class="pi-loader">
                    <div class="pi-loader__dots">
                        <span></span><span></span><span></span>
                    </div>
                </div>
            </transition>

            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link
                rel="preconnect"
                href="https://fonts.gstatic.com"
                crossorigin
            />

            <!-- Change template styling if mode is not fullscreen -->
            <div v-if="!fullscreen && standaloneJson.length > 0">
                <standaloneTemplate
                    ref="standaloneTemplateComponent"
                    :standaloneJson="standaloneJson"
                    :templateId="sourceTemplateId"
                    :isOwner="isOwner"
                    :isAdmin="isAdmin"
                    @save-template="saveTemplateJsonData"
                />
            </div>
            <!-- END Change template styling if mode is not fullscreen -->

            <div
                class="flex flex-wrap"
                :style="{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.3s ease' }"
            >
                <MasterEditButton v-if="isOwner && !isStandaloneView" />
                <AddSection
                    v-if="isOwner && !isStandaloneView"
                    :templateSections="templateMock"
                    @add-section="addSection"
                />

                <button
                    v-if="isOwner && !isStandaloneView"
                    :onClick="saveTemplateJsonData"
                    class="fixed z-20 top-7 md:top-10 btn md:btn-md text-xs md:text-base btn-primary transition-all duration-500"
                    :class="
                        editMode
                            ? 'left-20 md:left-24 btn-sm opacity-100'
                            : 'left-10 md:left-10 btn-sm opacity-0 pointer-events-none'
                    "
                >
                    <font-awesome-icon icon="fa-solid fa-cloud-arrow-up" />
                    Salveaza
                </button>

                <div
                    class="w-full relative"
                    v-for="(section, sectionKey) in templateMock"
                    :key="sectionKey"
                >
                    <!-- Section edit button (top-left, standalone only) -->
                    <button
                        v-if="isOwner && isStandaloneView"
                        class="section-edit-btn"
                        :class="{ 'section-edit-btn--active': editMode }"
                        @click.stop="$store.dispatch('toggleEditMode')"
                    >
                        <font-awesome-icon v-if="editMode" icon="fa-solid fa-xmark" />
                        <font-awesome-icon v-else icon="fa-solid fa-pen" />
                        <span>{{ editMode ? 'Inchide' : 'Editeaza' }}</span>
                    </button>

                    <!-- Floating Images Component -->
                    <FloatingImages
                        v-if="section.showFloatingBaloons && !isStandaloneView"
                        :images="imageData"
                    />
                    <!-- sidebar menu     -->
                    <div
                        v-if="isOwner"
                        class="absolute z-20 left-5 md:left-10 top-40 md:top-40 flex flex-col gap-2.5 transition-all"
                        :class="
                            editMode
                                ? 'opacity-100'
                                : 'opacity-0 pointer-events-none'
                        "
                    >
                        <BackgroundImageEditor
                            v-if="!isStandaloneView"
                            :value="section.backgroundImage.value"
                            :section="section"
                            :sectionKey="sectionKey"
                            propertyKey="backgroundImage.value"
                            :config="section.backgroundImage._config"
                            @edit-background-image="
                                setEditedKeys(sectionKey, 'backgroundImage')
                            "
                        />

                        <BackgroundColorEditor
                            v-if="!isStandaloneView"
                            :value="section.backgroundColor.value"
                            :section="section"
                            :sectionKey="sectionKey"
                            propertyKey="backgroundColor.value"
                            :config="section.backgroundColor._config"
                            @update-section="updateSection"
                        />

                        <MaskedImagesEditor
                            v-if="!isStandaloneView"
                            :section="section"
                            :sectionKey="sectionKey"
                            @edit-image="setEditedKeys"
                            @delete-masked-images="delete section.maskedImages"
                            @update-section="updateSection"
                        />

                        <CountdownTimerEditor
                            v-if="!isStandaloneView"
                            :section="section"
                            :sectionKey="sectionKey"
                            @update-section="updateSection"
                        />

                        <LocationElementsEditor
                            v-if="!isStandaloneView"
                            :section="section"
                            :sectionKey="sectionKey"
                            @edit-image="setEditedKeys"
                            @update-section="updateSection"
                        />

                        <SectionDividerEditor
                            v-if="!isStandaloneView"
                            :section="section"
                            :sectionKey="sectionKey"
                            :nextSectionBgColor="getNextSectionBgColor(sectionKey)"
                            @update-section="updateSection"
                        />

                        <UpdateSectionOrder
                            v-if="!isStandaloneView"
                            :section="section"
                            :sectionKey="sectionKey"
                            :sectionCount="getSectionCount()"
                            @update-order="updateSectionOrder"
                        />

                        <DeleteSection
                            v-if="!isStandaloneView"
                            :section="section"
                            :sectionKey="String(sectionKey)"
                            @delete-section="deleteSection(sectionKey)"
                        />
                    </div>
                    <div
                        class="md:px-10 bg-cover bg-center parallax-section"
                        :class="section.plainImage ? 'py-0 px-0' : 'py-20 px-5'"
                        :style="{
                            backgroundImage: sectionBackground(section),
                            backgroundColor: section.backgroundColor.value,
                            minHeight: section.plainImage
                                ? 'auto'
                                : section.minHeight._value,
                            position: 'relative',
                            overflow: 'hidden',
                        }"
                    >
                        <div class="relative z-10">
                            <!-- Dynamic text blocks (for non-form sections) -->
                            <template v-if="section.textBlocks && !(section._sectionType && section._sectionType.value === 'form')">
                                <div
                                    v-for="(block, blockIdx) in section.textBlocks"
                                    :key="blockIdx"
                                    class="fs-text-block"
                                >
                                    <p
                                        :style="{
                                            fontFamily: block.fontFamily,
                                            fontSize: fluidFontSize(block.fontSize),
                                            color: block.textColor,
                                            marginTop: `${block.marginTop}vh`,
                                            'text-shadow': block.textShadow === true || block.textShadow === 'true' ? textShadow : '',
                                            cursor: editMode ? 'pointer' : 'default',
                                        }"
                                        :class="[block.cssClass]"
                                        v-html="block.text.replace(/\n/g, '<br>')"
                                        @click.stop="openTextEditor(`tb-${sectionKey}-${blockIdx}`)"
                                    ></p>
                                    <SectionTextEditBar
                                        v-if="isOwner"
                                        :ref="`tb-${sectionKey}-${blockIdx}`"
                                        :textValue="block.text"
                                        :colorValue="block.textColor"
                                        :sizeValue="block.fontSize"
                                        :shadowValue="block.textShadow"
                                        :marginValue="block.marginTop"
                                        :fontValue="block.fontFamily"
                                        :fonts="fontFamilyCSS"
                                        :section="section"
                                        :sectionKey="sectionKey"
                                        :textPropertyKey="`textBlocks.${blockIdx}.text`"
                                        :colorPropertyKey="`textBlocks.${blockIdx}.textColor`"
                                        :sizePropertyKey="`textBlocks.${blockIdx}.fontSize`"
                                        :shadowPropertyKey="`textBlocks.${blockIdx}.textShadow`"
                                        :marginPropertyKey="`textBlocks.${blockIdx}.marginTop`"
                                        :fontPropertyKey="`textBlocks.${blockIdx}.fontFamily`"
                                        :marginConfig="{ _min: '0', _max: '70' }"
                                        :deletable="section.textBlocks.length > 1"
                                        @update-section="updateSection"
                                        @delete-block="removeTextBlock(sectionKey, blockIdx)"
                                    />
                                </div>
                                <button
                                    v-if="editMode && isOwner"
                                    class="fs-add-text-btn"
                                    @click.stop="addTextBlock(sectionKey)"
                                >
                                    <font-awesome-icon icon="fa-solid fa-plus" />
                                    <span>Adauga text</span>
                                </button>
                            </template>

                            <!-- Form sections: keep legacy title + description (rendered by InvitationFormConfigurable) -->
                            <template v-else-if="!(section._sectionType && section._sectionType.value === 'form')">
                                <!-- Fallback for old sections without textBlocks (should not happen after migration) -->
                                <div v-if="section.title">
                                    <p
                                        :style="{
                                            fontFamily: section.title.fontFamily._value,
                                            fontSize: fluidFontSize(section.title.fontSize.value),
                                            color: section.title.textColor.value,
                                            marginTop: `${section.title.marginTop.value}vh`,
                                            'text-shadow': (section.title.text.textShadow && section.title.text.textShadow == true) || section.title.text.textShadow == 'true' ? textShadow : '',
                                        }"
                                        :class="[section.title._CSSclass.value]"
                                        v-html="section.title.text.value.replace(/\n/g, '<br>')"
                                    ></p>
                                </div>
                                <div v-if="section.description">
                                    <p
                                        :style="{
                                            fontFamily: section.description.fontFamily._value,
                                            fontSize: fluidFontSize(section.description.fontSize.value),
                                            color: section.description.textColor.value,
                                            marginTop: `${section.description.marginTop.value}vh`,
                                            'text-shadow': section.description.text.textShadow ? textShadow : '',
                                        }"
                                        :class="[section.description._CSSclass.value]"
                                        v-html="section.description.text.value.replace(/\n/g, '<br>')"
                                    ></p>
                                </div>
                            </template>

                            <!-- Masked Images Start -->
                            <div
                                v-if="section.maskedImages && !isStandaloneView"
                                class="flex w-full justify-center space-x-10 mt-10 drop-shadow-lg"
                            >
                                <img
                                    class="mask mask-heart h-28 md:h-40"
                                    :src="`${section.maskedImages.image1}`"
                                />
                                <img
                                    class="mask mask-heart h-28 md:h-40"
                                    :src="`${section.maskedImages.image2}`"
                                />
                            </div>
                            <!-- Masked Images End -->

                            <!-- Location Items Start -->
                            <div
                                v-if="section.locationElements && !isStandaloneView"
                                class="loc-cards-grid"
                            >
                                <template
                                    v-for="(location, locationKey) in section.locationElements"
                                    :key="locationKey"
                                >
                                    <div
                                        class="loc-card-public"
                                        v-if="location.visible === true || location.visible === 'true'"
                                    >
                                        <figure v-if="location.image" class="loc-card-public__figure">
                                            <img
                                                :src="location.image"
                                                alt="locatie"
                                                class="loc-card-public__img"
                                            />
                                        </figure>
                                        <div class="loc-card-public__body">
                                            <h3 v-if="location.locationTitle" class="loc-card-public__title">
                                                {{ location.locationTitle }}
                                            </h3>
                                            <div v-if="location.date" class="loc-card-public__detail">
                                                <font-awesome-icon icon="fa-solid fa-calendar" class="loc-card-public__icon" />
                                                <span>{{ location.date }}</span>
                                            </div>
                                            <div v-if="location.locationName" class="loc-card-public__detail">
                                                <font-awesome-icon icon="fa-solid fa-location-dot" class="loc-card-public__icon" />
                                                <span>{{ location.locationName }}</span>
                                            </div>
                                            <div v-if="location.locationAddress" class="loc-card-public__detail loc-card-public__detail--muted">
                                                <span>{{ location.locationAddress }}</span>
                                            </div>
                                            <a
                                                v-if="location.locationCoordonates"
                                                class="loc-card-public__map-btn"
                                                target="_blank"
                                                :href="`https://www.google.com/maps?q=${location.locationCoordonates}`"
                                            >
                                                <font-awesome-icon icon="fa-solid fa-map-location-dot" />
                                                <span>Vezi pe harta</span>
                                            </a>
                                        </div>
                                    </div>
                                </template>
                            </div>

                            <div v-if="section.countdownActive && !isStandaloneView" class="mt-10">
                                <Countdown
                                    :deadlineDate="projectDate"
                                    :labels="{
                                        days: 'Zile',
                                        hours: 'Ore',
                                        minutes: 'Minute',
                                        seconds: 'Secunde',
                                    }"
                                    :labelColor="'#ffffff'"
                                    :mainColor="'#ffffff'"
                                />
                            </div>

                            <!-- Location Items End -->
                            <template
                                v-if="section._sectionType.value === 'form'"
                            >
                                <InvitationForm
                                    :projectId="projectId"
                                    :invitationId="invitationId"
                                    :section="section"
                                    :sectionKey="sectionKey"
                                    :isOwner="isOwner"
                                    :is-simple="isSimple"
                                    :recipientName="recipientName"
                                    :recipientSurname="recipientSurname"
                                    :hasResponded="hasResponded"
                                    :formUnlocked="formUnlocked"
                                    :isStandalone="isStandaloneView"
                                    :fonts="fontFamilyCSS"
                                    @update-section="updateSection"
                                    @open-text-editor="openTextEditor"
                                />
                            </template>
                        </div>

                        <img
                            class="v-full"
                            v-if="section.plainImage"
                            :src="`${section.backgroundImage.value}`"
                        />

                        <!-- Section divider (overlays bottom of this section) -->
                        <SectionDivider
                            v-if="section.divider && section.divider.type && !isStandaloneView"
                            :type="section.divider.type"
                            :color="section.divider.color || '#ffffff'"
                            :gradientFrom="section.backgroundColor.value || 'transparent'"
                            :flip="!!section.divider.flip"
                        />
                    </div>
                </div>
            </div>

            <!-- Display images -->
            <div
                :class="{
                    fixed: !imageModalClosed,
                    hidden: imageModalClosed,
                }"
                class="inset-0 flex md:items-center justify-center static z-50 overflow-y-hidden"
            >
                <div class="absolute bg-black opacity-50 inset-0 z-50"></div>
                <div
                    class="z-50 relative rounded-xl shadow-lg w-full md:w-1/2 h-full md:h-2/3 overflow-auto"
                    :class="
                        image.src && !fileUploadInProgress
                            ? 'bg-black'
                            : 'bg-white'
                    "
                >
                    <div
                        v-if="fileUploadInProgress"
                        class="absolute h-full w-full flex justify-center align-middle"
                    >
                        <div class="loading loading-bars loading-lg"></div>
                    </div>

                    <div class="p-10">
                        <font-awesome-icon
                            icon="fa-solid fa-circle-xmark"
                            class="absolute right-5 top-5 text-3xl hover:text-red-300 cursor-pointer"
                            @click="toggleImageModal()"
                        />

                        <!-- Modal content -->
                        <div
                            class="w-full left-0 top-0 z-30 h-3/4 absolute"
                            :class="{ hidden: fileUploadInProgress }"
                            v-if="image.src"
                        >
                            <cropper
                                ref="cropper"
                                class="upload-cropper"
                                :src="image.src"
                                :stencil-props="{
                                    movable: cropperOptions.movable,
                                    resizable: cropperOptions.resizable,
                                }"
                            />
                            <div class="actions mt-5">
                                <button
                                    @click="image.src = null"
                                    class="btn btn-outline btn-base-100"
                                >
                                    Anuleaza
                                </button>
                                <button
                                    @click="crop"
                                    class="btn btn-primary ml-10"
                                    :class="{
                                        'btn-disabled': fileUploadInProgress,
                                    }"
                                >
                                    Salveaza
                                </button>
                            </div>
                        </div>

                        <div v-if="!image.src">
                            <h1 class="text-2xl font-bold mb-4">
                                Galerie Foto
                            </h1>
                            <p class="mb-5">
                                Adauga sau selecteaza imagini din galeria ta
                            </p>
                            <div class="mb-10" v-if="!isTemplateEdit">
                                <label for="large-file-input" class="sr-only"
                                    >Choose file</label
                                >
                                <div
                                    id="large-file-input"
                                    class="block cursor-pointer transition relative w-full border border-green-800 bg-green-600 text-white shadow hover:shadow-xl rounded-lg text-sm focus:z-10 disabled:opacity-50 disabled:pointer-events-none file:bg-gray-50 file:border-0 file:me-4 file:py-3 file:px-4 file:sm:py-5"
                                >
                                    <div
                                        class="h-16 cursor-pointer flex text-xl items-center px-5"
                                        @click="$refs.file.click()"
                                    >
                                        <div class=" ">
                                            <font-awesome-icon
                                                icon="fa-solid fa-cloud-arrow-up"
                                                class="mr-10 ml-5"
                                            />
                                        </div>
                                        <div class="font-bold">
                                            Incarca imagine
                                        </div>
                                    </div>

                                    <input
                                        type="file"
                                        name="large-file-input"
                                        @change="loadImage($event)"
                                        accept="image/png, image/jpeg"
                                        class="absolute cursor-pointer opacity-0 left-0 top-0 w-full border border-gray-200 shadow-sm rounded-lg text-sm focus:z-10 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:border-gray-700 dark:text-gray-400 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600 file:bg-gray-50 file:border-0 file:me-4 file:py-3 file:px-4 file:sm:py-5 dark:file:bg-gray-700 dark:file:text-gray-400"
                                    />
                                </div>
                            </div>

                            <div
                                class="block border border-b-2 p-5 mb-5 relative rounded-lg"
                            >
                                <button
                                    v-if="
                                        templateMock &&
                                        templateMock[editedSectionKey] &&
                                        templateMock[editedSectionKey]
                                            .backgroundImage &&
                                        templateMock[editedSectionKey]
                                            .backgroundImage.value
                                    "
                                    class="mb-10 btn btn-xs btn-error absolute right-2 top-2"
                                    @click="removeBackgroundImage"
                                >
                                    <font-awesome-icon
                                        icon="fa-solid fa-xmark"
                                    />
                                    Sterge Imaginea de fundal
                                </button>
                                <div class="font-bold mb-5">
                                    Imaginile noastre
                                </div>
                                <div
                                    class="grid grid-cols-2 md:grid-cols-4 gap-4"
                                >
                                    <div
                                        v-for="(image, index) in templateImages"
                                        :key="index"
                                    >
                                        <div>
                                            <img
                                                :src="`${image}`"
                                                class="h-auto max-w-full rounded-lg shadow-md"
                                                alt="Project Image"
                                            />
                                        </div>

                                        <button
                                            class="bg-green-500 mr-2 text-white px-4 py-2 rounded hover:bg-green-600 mt-5"
                                            @click="selectImage(image)"
                                        >
                                            Selecteaza
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="block border p-5 rounded-lg">
                                <div class="font-bold mb-5">Imaginile tale</div>
                                <div
                                    class="grid grid-cols-2 md:grid-cols-4 gap-4"
                                >
                                    <div v-if="imagesData.length == 0">
                                        Nu ai nici o imagine disponibila.
                                    </div>
                                    <div
                                        v-for="(image, index) in imagesData"
                                        :key="index"
                                    >
                                        <div>
                                            <img
                                                :src="`${image}`"
                                                class="h-auto max-w-full rounded-lg shadow-md"
                                                alt="Project Image"
                                            />
                                        </div>

                                        <button
                                            class="bg-green-500 mr-2 text-white px-4 py-2 rounded hover:bg-green-600 mt-5"
                                            @click="selectImage(image)"
                                        >
                                            Selecteaza
                                        </button>
                                        <button
                                            class="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 mt-5"
                                            @click="deleteImage(image)"
                                        >
                                            Sterge
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import html2canvas from 'html2canvas';
import InvitationForm from '@/components/InvitationFormConfigurable.vue';
import { useToast } from 'vue-toastification';
import { Countdown } from 'vue3-flip-countdown';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import { mapGetters } from 'vuex';

import FloatingImages from '@/components/FloatingImages.vue';

import MasterEditButton from '@/components/editorUtilsInline/MasterEditButton.vue';
import AddSection from '@/components/editorUtilsInline/AddSection.vue';

// Section sidebar
import BackgroundImageEditor from '@/components/editorUtilsInline/sectionSidebar/BackgroundImageEditor.vue';
import BackgroundColorEditor from '@/components/editorUtilsInline/sectionSidebar/BackgroundColorEditor.vue';
import MaskedImagesEditor from '@/components/editorUtilsInline/sectionSidebar/MaskedImagesEditor.vue';
import CountdownTimerEditor from '@/components/editorUtilsInline/sectionSidebar/CountdownTimerEditor.vue';
import LocationElementsEditor from '@/components/editorUtilsInline/sectionSidebar/LocationElementsEditor.vue';
import DeleteSection from '@/components/editorUtilsInline/sectionSidebar/DeleteSection.vue';
import UpdateSectionOrder from '@/components/editorUtilsInline/sectionSidebar/UpdateSectionOrder.vue';
import SectionDividerEditor from '@/components/editorUtilsInline/sectionSidebar/SectionDividerEditor.vue';

import SectionDivider from '@/components/SectionDivider.vue';

// Text tools (unified)
import SectionTextEditBar from '@/components/editorUtilsInline/SectionTextEditBar.vue';

import standaloneTemplate from '@/components/templates/standaloneTemplate.vue';

// This function is used to detect the actual image type,
function getMimeType(file, fallback = null) {
    const byteArray = new Uint8Array(file).subarray(0, 4);
    let header = '';
    for (let i = 0; i < byteArray.length; i++) {
        header += byteArray[i].toString(16);
    }
    switch (header) {
        case '89504e47':
            return 'image/png';
        case '47494638':
            return 'image/gif';
        case 'ffd8ffe0':
        case 'ffd8ffe1':
        case 'ffd8ffe2':
        case 'ffd8ffe3':
        case 'ffd8ffe8':
            return 'image/jpeg';
        default:
            return fallback;
    }
}

export default {
    name: 'PublicInvitation',
    components: {
        InvitationForm,
        Cropper,
        Countdown,
        MasterEditButton,
        BackgroundImageEditor,
        BackgroundColorEditor,
        MaskedImagesEditor,
        CountdownTimerEditor,
        LocationElementsEditor,
        DeleteSection,
        AddSection,
        UpdateSectionOrder,
        SectionDividerEditor,
        SectionDivider,
        FloatingImages,
        standaloneTemplate,
        SectionTextEditBar,
    },
    props: {
        isSimple: {
            default: false,
        },
        isTemplateEdit: {
            default: false,
        },
    },
    computed: {
        ...mapGetters(['isEditMode', 'user']),
        editMode() {
            return this.isEditMode;
        },
        isStandaloneView() {
            return !this.fullscreen && this.standaloneJson.length > 0;
        },
        isAdmin() {
            return this.user && this.user.user_role === 'superAdmin';
        },
        contentReady() {
            if (!this.dataReady) return false;
            if (this.isStandaloneView) return true;
            return this.fontsReady;
        },
    },
    data() {
        return {
            standaloneJson: [],
            sourceTemplateId: null,
            fileUploadInProgress: false,
            isMobile: false,
            image: {
                src: null,
                type: null,
                fileName: null,
            },
            text: '',
            cropperOptions: {
                aspectRatio: null,
                movable: true,
                resizable: true,
            },
            projectId: null,
            powerAdmin: false,
            projectName: null,
            invitationId: null,
            recipientName: '',
            recipientSurname: '',
            hasResponded: false,
            formUnlocked: false,
            templateContent: null,
            templateId: null,
            isOwner: false,
            imagesData: [],
            templateImages: [],
            imageModalClosed: true,
            editedSectionKey: null,
            editedPropertyKey: null,
            editedNestedPropertyKey: null,
            editedValueKey: null,
            editedSubPropertyKey: null,
            templateMock: {},
            textShadow:
                '3px 5px 7px rgba(0, 0, 0, 1), 1px -1px 2px rgba(69, 65, 75, 0.39)',
            fontFamilyList: [],
            fontFamilyCSS: [],
            changesDetected: false,
            removeImageSelectedSection: null,
            imageData: [
                {
                    src: 'project-images/baloon-1.png',
                    alt: 'Image 1',
                    parallax: 0.5,
                    floatSpeed: 2,
                    style: { top: '40%', left: '10%' },
                },
                {
                    src: 'project-images/baloon-1.png',
                    alt: 'Image 2',
                    parallax: 0.3,
                    floatSpeed: 3,
                    style: { top: '30%', left: '30%' },
                },
                {
                    src: 'project-images/baloon-1.png',
                    alt: 'Image 3',
                    parallax: 0.7,
                    floatSpeed: 1.5,
                    style: { top: '50%', left: '70%' },
                },
                {
                    src: 'project-images/baloon-1.png',
                    alt: 'Image 3',
                    parallax: 0.7,
                    floatSpeed: 4.5,
                    style: { top: '40%', left: '60%' },
                },
            ],
            fullscreen: true,
            fontsReady: false,
            dataReady: false,
            errorState: null,
            errorMessage: '',
        };
    },
    mounted() {
        window.addEventListener('scroll', this.handleScroll);
        // Check if the user is on a mobile device
        this.isMobile = window.innerWidth <= 767; // Adjust the threshold based on your design
        // Listen for window resize events to update the isMobile flag
        window.addEventListener('resize', this.handleResize);

    },
    unmounted() {
        // Revoke the object URL, to allow the garbage collector to destroy the uploaded before file
        if (this.image.src) {
            URL.revokeObjectURL(this.image.src);
        }

        window.removeEventListener('scroll', this.handleScroll);

        // Remove the event listener to avoid memory leaks
        window.removeEventListener('resize', this.handleResize);

    },
    setup() {
        // Get toast interface
        const toast = useToast();

        // Make it available inside methods
        return { toast };
    },
    created() {
        const currentRoute = this.$route;
        // Access the route parameters
        this.projectId = currentRoute.params.projectId;
        this.projectName = currentRoute.params.projectName;
        this.invitationId = currentRoute.params.invitationId;

        // Check if the user viewing the page is the actual owner to avoid opened_time registration
        if (localStorage.getItem('ownedProjects')) {
            const ownedProjects = JSON.parse(
                localStorage.getItem('ownedProjects')
            );
            const targetProjectId = this.projectId;
            const isInOwnedProjects = ownedProjects.some(
                (project) => project.project_id == targetProjectId
            );

            if (isInOwnedProjects) {
                this.isOwner = true;
                this.$store.dispatch('fetchUserDetails');
            }
        }

        if (this.isTemplateEdit) {
            this.isOwner = true;
            this.$store.dispatch('fetchUserDetails');
            this.getTemplateData(this.$route.params.templateId);
        } else {
            this.getTemplateData();
        }

        this.handleScroll();
    },
    watch: {
        // templateMock: {
        //     handler(newVal, oldVal) {
        //         this.onJsonDataUpdated(newVal, oldVal);
        //     },
        //     deep: true,
        // },
    },
    methods: {
        // onJsonDataUpdated(newVal, oldVal) {
        //     // Your logic to handle the update
        //     // console.log('JSON data updated oldVal:', oldVal);
        //     console.log('JSON data updated newVal:', newVal);
        //     console.log('JSON data updated oldVal:', oldVal);
        //     console.log('templateMock', this.templateMock);
        //     if (this.templateMock && oldVal && this.templateMock === oldVal) {
        //         this.changesDetected = true;
        //     }
        //     console.log(this.templateMock === oldVal);
        // },
        migrateToTextBlocks() {
            if (!this.templateMock) return;
            Object.keys(this.templateMock).forEach(key => {
                const section = this.templateMock[key];
                // Skip form sections — they keep title+description for InvitationFormConfigurable
                if (section._sectionType && section._sectionType.value === 'form') return;
                // Skip if already migrated
                if (section.textBlocks) return;
                // Skip if no title/description at all
                if (!section.title && !section.description) return;

                const blocks = [];
                if (section.title && section.title.text && section.title.text.value) {
                    blocks.push({
                        text: section.title.text.value,
                        fontFamily: section.title.fontFamily?._value || "'Dancing Script', cursive",
                        fontSize: section.title.fontSize?.value || '40',
                        textColor: section.title.textColor?.value || '#000000',
                        marginTop: section.title.marginTop?.value || '10',
                        textShadow: section.title.text.textShadow || false,
                        cssClass: section.title._CSSclass?.value || '',
                    });
                }
                if (section.description && section.description.text && section.description.text.value) {
                    blocks.push({
                        text: section.description.text.value,
                        fontFamily: section.description.fontFamily?._value || "'Dancing Script', cursive",
                        fontSize: section.description.fontSize?.value || '20',
                        textColor: section.description.textColor?.value || '#000000',
                        marginTop: section.description.marginTop?.value || '5',
                        textShadow: section.description.text.textShadow || false,
                        cssClass: section.description._CSSclass?.value || '',
                    });
                    // Migrate value2 and value3 as separate blocks
                    if (section.description.text.value2) {
                        blocks.push({
                            text: section.description.text.value2,
                            fontFamily: section.description.fontFamily?._value || "'Dancing Script', cursive",
                            fontSize: section.description.fontSize?.value || '20',
                            textColor: section.description.textColor?.value || '#000000',
                            marginTop: section.description.marginTop?.value || '5',
                            textShadow: section.description.text.textShadow || false,
                            cssClass: section.description._CSSclass?.value || '',
                        });
                    }
                    if (section.description.text.value3) {
                        blocks.push({
                            text: section.description.text.value3,
                            fontFamily: section.description.fontFamily?._value || "'Dancing Script', cursive",
                            fontSize: section.description.fontSize?.value || '20',
                            textColor: section.description.textColor?.value || '#000000',
                            marginTop: section.description.marginTop?.value || '5',
                            textShadow: section.description.text.textShadow || false,
                            cssClass: section.description._CSSclass?.value || '',
                        });
                    }
                }
                if (blocks.length > 0) {
                    section.textBlocks = blocks;
                }
            });
        },
        addTextBlock(sectionKey) {
            const section = this.templateMock[sectionKey];
            if (!section.textBlocks) section.textBlocks = [];
            section.textBlocks.push({
                text: 'Text nou',
                fontFamily: "'Dancing Script', cursive",
                fontSize: '24',
                textColor: '#000000',
                marginTop: '2',
                textShadow: false,
                cssClass: '',
            });
        },
        removeTextBlock(sectionKey, blockIdx) {
            const section = this.templateMock[sectionKey];
            if (section.textBlocks && section.textBlocks.length > 1) {
                section.textBlocks.splice(blockIdx, 1);
            }
        },
        sectionBackground(section) {
            const layers = [];
            // Gradient overlay (if set)
            if (section.backgroundGradient) {
                layers.push(section.backgroundGradient);
            }
            // Background image
            if (!section.plainImage && section.backgroundImage && section.backgroundImage.value) {
                layers.push(`url('${section.backgroundImage.value}')`);
            }
            return layers.length ? layers.join(', ') : 'none';
        },
        fluidFontSize(sizePx) {
            const s = Number(sizePx);
            if (!s || s <= 24) return `${s}px`;
            // Scale: mobile min = 50% of desktop, fluid between 320–1024px viewport
            const min = Math.round(s * 0.5);
            const preferred = (s / 1024 * 100).toFixed(2);
            return `clamp(${min}px, ${preferred}vw, ${s}px)`;
        },
        openTextEditor(refName) {
            if (!this.editMode) return;
            let comp = this.$refs[refName];
            if (Array.isArray(comp)) comp = comp[0];
            if (comp && comp.open) {
                comp.open();
            }
        },
        collectUsedFontNames(apiFonts) {
            const usedNames = new Set();
            // Generic font families that don't need @font-face loading
            const generics = new Set(['sans-serif', 'serif', 'monospace', 'cursive']);

            // Build a lookup: short key (e.g. 'alex-brush') -> css_value (e.g. 'Alex Brush')
            const keyToCssValue = {};
            if (apiFonts && apiFonts.length) {
                apiFonts.forEach(f => {
                    const key = f.css_value.toLowerCase().replace(/\s+/g, '-');
                    keyToCssValue[key] = f.css_value;
                });
            }

            // Extract from section titles, descriptions, and textBlocks
            if (this.templateMock) {
                Object.values(this.templateMock).forEach(section => {
                    if (section?.title?.fontFamily?._value) {
                        const raw = section.title.fontFamily._value;
                        const name = raw.split(',')[0].replace(/'/g, '').trim();
                        if (name) usedNames.add(name);
                    }
                    if (section?.description?.fontFamily?._value) {
                        const raw = section.description.fontFamily._value;
                        const name = raw.split(',')[0].replace(/'/g, '').trim();
                        if (name) usedNames.add(name);
                    }
                    if (Array.isArray(section?.textBlocks)) {
                        section.textBlocks.forEach(block => {
                            if (block.fontFamily) {
                                const name = block.fontFamily.split(',')[0].replace(/'/g, '').trim();
                                if (name) usedNames.add(name);
                            }
                        });
                    }
                });
            }

            // Extract from standalone canvas text elements
            let textEls = [];
            try {
                const sj = this.standaloneJson;
                const parsed = typeof sj === 'string' ? JSON.parse(sj) : sj;
                if (Array.isArray(parsed)) {
                    textEls = parsed;
                } else if (parsed && Array.isArray(parsed.textElements)) {
                    textEls = parsed.textElements;
                }
            } catch (_) { /* ignore parse errors */ }
            textEls.forEach(el => {
                if (el && el.fontFamily) {
                    // Strip stray quotes from stored data
                    const clean = el.fontFamily.replace(/'/g, '').trim();
                    if (!generics.has(clean)) {
                        // Canvas stores short keys like 'alex-brush'; resolve to css_value
                        const resolved = keyToCssValue[clean] || clean;
                        usedNames.add(resolved);
                    }
                }
            });

            // Filter out non-font values
            usedNames.delete('default');
            return Array.from(usedNames);
        },
        async loadFontFamily() {
            let apiFonts = [];
            try {
                // Fetch active fonts list from API (lightweight JSON, no files)
                const res = await axios.get('/api/index.php?endpoint=fonts');
                if (res.data && res.data.success && Array.isArray(res.data.data)) {
                    apiFonts = res.data.data;
                    this.fontFamilyCSS = apiFonts.map(f => `'${f.css_value}', ${f.category}`);
                }
            } catch (err) {
                console.error('Failed to fetch fonts from API', err);
            }

            // Load @font-face CSS — selective for viewers, all for editors
            const existing = document.getElementById('app-fonts-css');
            if (existing) existing.remove();

            let cssUrl = '/api/index.php?endpoint=fontsCSS';

            if (!this.isOwner) {
                // Public viewer — only load fonts actually used in this invitation
                const usedNames = this.collectUsedFontNames(apiFonts);
                if (usedNames.length > 0) {
                    cssUrl += '&names=' + usedNames.map(n => encodeURIComponent(n)).join(',');
                }
            }

            const link = document.createElement('link');
            link.id = 'app-fonts-css';
            link.rel = 'stylesheet';
            link.href = cssUrl;
            document.head.appendChild(link);

            // Wait for fonts to finish loading, then reveal content.
            // Safety timeout (5s) so the loader never hangs indefinitely.
            const fontsPromise = document.fonts.ready;
            const timeout = new Promise(r => setTimeout(r, 5000));
            Promise.race([fontsPromise, timeout]).then(() => {
                this.fontsReady = true;
            });
        },
        handleScroll() {
            // Skip parallax on mobile — touch scroll momentum makes it janky
            if (this.isMobile) return;

            const parallaxSections =
                document.querySelectorAll('.parallax-section');

            parallaxSections.forEach((section) => {
                const rect = section.getBoundingClientRect();
                const distanceFromTop = rect.top;

                // Adjust the speed and offset as needed
                const speed = section.dataset.speed || 0.2;
                const offset = section.dataset.offset || 0;

                // Calculate the new background position based on the distance from the top
                const yPos = -(distanceFromTop * speed) + offset;

                section.style.backgroundPosition = `50% ${yPos}px`;
            });
        },
        crop() {
            let { canvas } = this.$refs.cropper.getResult();
            if (canvas) {
                canvas.toBlob((blob) => {
                    this.handleFileUpload(blob, this.image.fileName);

                    // Perhaps you should add the setting appropriate file format here
                }, this.image.type);
            }
        },
        loadImage(event) {
            // Reference to the DOM input element
            const { files } = event.target;
            const fileName = this.getFileNameFromPath(event.target.value);
            // Ensure that you have a file before attempting to read it
            if (files && files[0] && fileName) {
                // 1. Revoke the object URL, to allow the garbage collector to destroy the uploaded before file
                if (this.image.src) {
                    URL.revokeObjectURL(this.image.src);
                }
                // 2. Create the blob link to the file to optimize performance:
                const blob = URL.createObjectURL(files[0]);

                // 3. The steps below are designated to determine a file mime type to use it during the
                // getting of a cropped image from the canvas. You can replace it them by the following string,
                // but the type will be derived from the extension and it can lead to an incorrect result:
                //
                // this.image = {
                //    src: blob;
                //    type: files[0].type
                // }

                // Create a new FileReader to read this image binary data
                const reader = new FileReader();
                // Define a callback function to run, when FileReader finishes its job
                reader.onload = (e) => {
                    // Note: arrow function used here, so that "this.image" refers to the image of Vue component
                    this.image = {
                        // Set the image source (it will look like blob:http://example.com/2c5270a5-18b5-406e-a4fb-07427f5e7b94)
                        src: blob,
                        // Determine the image type to preserve it during the extracting the image from canvas:
                        type: getMimeType(e.target.result, files[0].type),
                        fileName: fileName,
                    };
                };
                // Start the reader job - read file as a data url (base64 format)
                reader.readAsArrayBuffer(files[0]);
            }
        },
        getFileNameFromPath(filePath) {
            // Split the path by "\" or "/" to handle different browsers
            const pathSegments = filePath.split(/[\\/]/);

            // Get the last segment, which is the filename
            const fileName = pathSegments[pathSegments.length - 1];

            return fileName;
        },
        setEditedKeys(sectionKey, propertyKey, valueKey, subPropertyKey) {
            this.editedSectionKey = sectionKey;
            this.editedPropertyKey = propertyKey;
            this.editedValueKey = valueKey || null;
            this.editedSubPropertyKey = subPropertyKey || null;

            this.toggleImageModal();
        },
        selectImage(image) {
            // Extract the underlying objects from Proxy
            const sectionKey = this.editedSectionKey;
            const propertyKey = this.editedPropertyKey;
            const valueKey = this.editedValueKey || 'value';
            const subPropertyKey = this.editedSubPropertyKey;

            if (
                sectionKey !== null &&
                propertyKey !== null &&
                this.templateMock[sectionKey] &&
                this.templateMock[sectionKey][propertyKey]
            ) {
                if (subPropertyKey) {
                    // Update the corresponding property in templateMock
                    this.templateMock[sectionKey][propertyKey][subPropertyKey][
                        valueKey
                    ] = image;
                } else {
                    // Update the corresponding property in templateMock
                    this.templateMock[sectionKey][propertyKey][valueKey] =
                        image;
                }

                this.toggleImageModal();
            } else {
                console.error('Invalid section or property.');
            }
        },

        toggleImageModal() {
            this.imageModalClosed = !this.imageModalClosed;
        },
        deleteImage(imagePath) {
            const formData = {
                imagePath: imagePath,
                projectId: this.projectId,
            };
            // Retrieve user_id from localStorage
            const jwtToken = localStorage.getItem('userToken');

            axios
                .post('/api/index.php?endpoint=deleteImage', formData, {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        Authorization: `Bearer ${jwtToken}`,
                    },
                })
                .then((result) => {
                    if (result.data.success) {
                        this.imagesData = this.imagesData.filter(
                            (image) => image !== imagePath
                        );
                    }
                })
                .catch((error) => {
                    console.error('Error uploading image', error);
                });
        },
        async handleFileUpload(file, fileName) {
            this.fileUploadInProgress = true;

            if (file && fileName) {
                console.log(file);
                // Check file type
                const allowedTypes = ['image/jpeg', 'image/png'];
                if (!allowedTypes.includes(file.type)) {
                    this.toast.error(
                        `Eroare! Imaginea trebuie sa fie in format JPG sau PNG.`
                    );

                    this.fileUploadInProgress = false;

                    return;
                }

                if (!this.powerAdmin) {
                    // Check file size
                    const maxSizeMB = 2;
                    const maxSizeBytes = maxSizeMB * 1024 * 1024; // Convert MB to bytes
                    if (file.size > maxSizeBytes) {
                        this.toast.error(
                            `Eroare! Imaginea trebuie sa nu depaseasca ${maxSizeMB}MB.`
                        );

                        this.fileUploadInProgress = false;
                        return;
                    }

                    // Check image dimensions
                    const maxWidth = 2000;
                    const maxHeight = 2000;
                    try {
                        await this.isValidImageDimensions(
                            file,
                            maxWidth,
                            maxHeight
                        );
                    } catch (error) {
                        this.toast.error(`Eroare! ${error}.`);

                        this.fileUploadInProgress = false;
                        return;
                    }
                }

                const formData = new FormData();
                // Retrieve user_id from localStorage
                const jwtToken = localStorage.getItem('userToken');

                formData.append('file', file, fileName);
                formData.append('projectId', this.projectId);

                axios
                    .post('/api/index.php?endpoint=uploadImage', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            Authorization: `Bearer ${jwtToken}`,
                        },
                    })
                    .then((response) => {
                        // Assuming the backend sends an object with imagePath property
                        const imagePath = response.data.imagePath;
                        // Append the new image path to the imagesData array
                        this.imagesData.push(imagePath);
                        // Handle the response from the backend, e.g., update the UI or store the image path.
                        this.image.src = null;

                        this.fileUploadInProgress = false;
                    })
                    .catch((error) => {
                        this.fileUploadInProgress = false;
                        console.error('Error uploading image', error);
                    });
            }
        },
        // Utility function to check if the image dimensions are valid
        isValidImageDimensions(file, maxWidth, maxHeight) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const img = new Image();
                    img.onload = () => {
                        if (img.width <= maxWidth && img.height <= maxHeight) {
                            resolve();
                        } else {
                            reject(
                                `Imaginea trebuie sa nu depaseasca ${maxWidth}x${maxHeight} px.`
                            );
                        }
                    };
                    img.src = e.target.result;
                };
                reader.readAsDataURL(file);
            });
        },
        // handlePreviewData(jsonData) {
        //     // Use this for real-time preview updates if needed
        //     // console.log('Preview JSON:', jsonData);
        // },
        //standaloneTemplateComponent
        async saveTemplateJsonData() {
            let standaloneJson = null;

            if (!this.fullscreen) {
                // Directly fetch the latest JSON data from the child
                standaloneJson =
                    this.$refs.standaloneTemplateComponent.getLatestJsonData();
                // Now send your AJAX post with the latest JSON payload
                // console.log('Saving JSON:', standaloneJson);
                // return false;
            }

            // console.log('data to be saved', JSON.stringify(this.templateMock));
            try {
                // Retrieve user_id from localStorage
                const jwtToken = localStorage.getItem('userToken');

                let endpoint = `/api/index.php?endpoint=saveTemplate`;

                // Assuming you have form data available in Vue data properties

                let formData = {
                    projectId: this.projectId,
                    contentData: this.templateMock,
                    templateType: 'fulscreen',
                };

                if (!this.fullscreen) {
                    // should save standalone json data in the template_data_standalone column
                    formData.templateType = 'standalone';
                    formData.standaloneJson = standaloneJson;
                }

                if (this.isTemplateEdit) {
                    endpoint = `/api/index.php?endpoint=enterpriseSaveTemplateEdit`;
                    formData['templateId'] = this.$route.params.templateId;
                }

                await axios
                    .post(
                        endpoint,
                        formData, // Pass form data in the request payload
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${jwtToken}`,
                            },
                        }
                    )
                    .then((result) => {
                        if (result.data.success) {
                            this.toast.success(`Continutul a fost actualizat!`);
                        } else {
                            this.toast.error(
                                `Continutul nu a putut fi adaugat`
                            );
                        }
                    })
                    .catch(() => {
                        this.toast.error(`Continutul nu a putut fi adaugat`);
                    });
            } catch (error) {
                console.error(
                    'Error updating template:',
                    error.response ? error.response.data : error.message
                );
            }
        },
        async getTemplateData(enterpriseTemplateId) {
            let endpoint = `/api/index.php?endpoint=getInvitationContent&projectId=${
                this.projectId
            }&isOwner=${this.isOwner}${
                this.isSimple
                    ? '&isSimple=true'
                    : '&invitationId=' + this.invitationId
            }${
                this.isTemplateEdit
                    ? '&isTemplateEdit=true&templateId=' + enterpriseTemplateId
                    : ''
            }`;

            if (this.isTemplateEdit && enterpriseTemplateId) {
                endpoint = `/api/index.php?endpoint=getEnterpriseTemplateContent&templateId=${enterpriseTemplateId}`;
            }

            const jwtToken = localStorage.getItem('userToken') || 0;
            axios
                .get(endpoint, {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${jwtToken}`,
                    },
                })
                .then((response) => {
                    if (response && response.data.success) {
                        // Assuming projects is an array, update the index based on your API response
                        const projectData = response.data.data.projects[0];
                        this.projectId = projectData.project_id;
                        this.projectName = projectData.project_name;
                        this.projectDate = projectData.project_date;
                        this.templateId = projectData.project_template;
                        this.sourceTemplateId = projectData.template_id;
                        this.templateContent = projectData.invitation_content;
                        this.templateMock = JSON.parse(this.templateContent);
                        // Migrate old title+description to textBlocks for non-form sections
                        this.migrateToTextBlocks();
                        this.imagesData = projectData.project_images || [];
                        this.templateImages = projectData.template_images;

                        this.standaloneJson = projectData.standalone_json;

                        // Set recipient identity & form lock state (complex invitations only)
                        if (this.isSimple) {
                            this.recipientName = '';
                            this.recipientSurname = '';
                            this.hasResponded = false;
                            this.formUnlocked = false;
                        } else if (response.data.data.recipient_name !== undefined) {
                            this.recipientName = response.data.data.recipient_name || '';
                            this.recipientSurname = response.data.data.recipient_surname || '';
                            this.hasResponded = !!response.data.data.has_responded;
                            this.formUnlocked = !!response.data.data.form_unlocked;
                        }

                        if (this.standaloneJson.length > 0) {
                            this.fullscreen = false;
                        }

                        if (this.$route.query.capture === 'true' && this.isTemplateEdit) {
                            this.waitForCaptureReady();
                        }

                        this.dataReady = true;

                        // Load fonts after data is available — selective for viewers, all for editors
                        this.loadFontFamily();
                    }

                    if (!response || !response.data || !response.data.success) {
                        const msg = response?.data?.message || '';
                        if (msg.toLowerCase().includes('inactive')) {
                            this.errorState = 'inactive';
                        } else {
                            this.errorState = 'not_found';
                        }
                        this.errorMessage = msg;
                    }
                })
                .catch(() => {
                    this.errorState = 'not_found';
                    this.errorMessage = '';
                });
        },
        updateSection(sectionKey, updatedSection, dbSave) {
            this.templateMock[sectionKey] = updatedSection;

            if (dbSave === true) {
                this.saveTemplateJsonData();
            }
        },

        addSection(nextSectionKey, newSection) {
            if (Object.keys(this.templateMock).length > 7) {
                this.toast.error(`Sunt permise maxim 7 sectiuni !`);
            } else {
                this.templateMock[nextSectionKey] = newSection;
                this.toast.success(`Sectiunea a fost adaugata!`);
                this.reorderSections();
            }
        },

        deleteSection(sectionKey) {
            delete this.templateMock[sectionKey];
        },

        updateSectionOrder(sectionKey, order) {
            this.templateMock[sectionKey].order = order;
            this.reorderSections();
        },

        removeBackgroundImage() {
            this.templateMock[this.editedSectionKey].backgroundImage.value = '';
        },

        reorderSections() {
            const sectionsArray = Object.entries(this.templateMock);
            sectionsArray.sort((a, b) => {
                const orderA =
                    a[1]['order'] !== undefined
                        ? a[1]['order']
                        : Number.MAX_SAFE_INTEGER;
                const orderB =
                    b[1]['order'] !== undefined
                        ? b[1]['order']
                        : Number.MAX_SAFE_INTEGER;
                return orderA - orderB;
            });
            this.templateMock = Object.fromEntries(sectionsArray);
        },

        getNextSectionBgColor(currentKey) {
            const keys = Object.keys(this.templateMock);
            const idx = keys.indexOf(String(currentKey));
            if (idx >= 0 && idx < keys.length - 1) {
                const nextSection = this.templateMock[keys[idx + 1]];
                return (nextSection && nextSection.backgroundColor && nextSection.backgroundColor.value) || '#ffffff';
            }
            return '#ffffff';
        },

        getSectionCount() {
            return Object.keys(this.templateMock).length;
        },
        waitForCaptureReady(attempt = 0) {
            let ready = false;
            if (this.isStandaloneView) {
                const comp = this.$refs.standaloneTemplateComponent;
                ready = comp && comp.$refs && comp.$refs.canvasContainer;
            } else {
                // Fullscreen: check that at least one section is rendered
                const container = this.$refs.projectsContainer;
                ready = container && container.querySelector('.parallax-section');
            }
            if (ready) {
                // Wait for fonts + extra render time
                document.fonts.ready.then(() => {
                    setTimeout(() => this.autoCaptureCover(), 2000);
                });
            } else if (attempt < 30) {
                setTimeout(() => this.waitForCaptureReady(attempt + 1), 500);
            } else {
                console.error('Capture: template never became ready after 15s');
                this.toast.error('Could not capture preview — template did not load.');
            }
        },
        async autoCaptureCover() {
            let blob = null;
            try {
                await document.fonts.ready;

                if (this.isStandaloneView) {
                    const comp = this.$refs.standaloneTemplateComponent;
                    if (!comp || typeof comp.capturePreview !== 'function') {
                        console.error('capturePreview not available, comp:', comp);
                        this.toast.error('Capture method not available.');
                        return;
                    }
                    blob = await comp.capturePreview();
                } else {
                    // Fullscreen: capture the first section as the cover
                    const container = this.$refs.projectsContainer;
                    const firstSection = container ? container.querySelector('.parallax-section') : null;
                    if (!firstSection) {
                        console.error('No section found for fullscreen capture');
                        this.toast.error('No section found to capture.');
                        return;
                    }
                    const canvas = await html2canvas(firstSection, {
                        useCORS: true,
                        allowTaint: true,
                        backgroundColor: '#ffffff',
                        scale: 1,
                        logging: false,
                        imageTimeout: 5000,
                        onclone: (clonedDoc) => {
                            const all = clonedDoc.querySelectorAll('*');
                            all.forEach(node => {
                                const cs = node.style;
                                if (!cs) return;
                                for (let i = cs.length - 1; i >= 0; i--) {
                                    const prop = cs[i];
                                    const val = cs.getPropertyValue(prop);
                                    if (val && val.includes('oklch')) {
                                        cs.setProperty(prop, 'transparent');
                                    }
                                }
                                const computed = clonedDoc.defaultView.getComputedStyle(node);
                                ['background-color', 'color', 'border-color', 'outline-color', 'box-shadow'].forEach(p => {
                                    const v = computed.getPropertyValue(p);
                                    if (v && v.includes('oklch')) {
                                        cs.setProperty(p, p === 'color' ? '#000000' : 'transparent');
                                    }
                                });
                            });
                        },
                    });
                    blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
                }

                if (!blob) {
                    console.error('Capture returned no blob');
                    this.toast.error('Capture returned empty image.');
                    return;
                }
                const jwtToken = localStorage.getItem('userToken');
                const templateId = this.$route.params.templateId;
                const formData = new FormData();
                formData.append('file', blob, 'preview.png');
                formData.append('templateId', templateId);
                const res = await axios.post(
                    '/api/index.php?endpoint=saveTemplateCover',
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            Authorization: `Bearer ${jwtToken}`,
                        },
                    }
                );
                if (res.data && res.data.success) {
                    if (window.opener) {
                        window.opener.postMessage({ type: 'coverCaptured', imagePath: res.data.imagePath, templateId }, '*');
                        window.close();
                    } else {
                        this.toast.success('Preview saved as template cover!');
                    }
                } else {
                    console.error('Save cover failed', res.data);
                    this.toast.error(res.data.message || 'Failed to save cover.');
                }
            } catch (err) {
                console.error('Auto capture error:', err.message, err);
                this.toast.error('Failed to capture preview: ' + err.message);
            }
        },
    },
};
</script>

<style>
.transition-height {
    transition: height 0.5s ease;
    max-height: 52rem; /* Set a maximum height if needed */
}

.parallax-section {
    background-position: 50% 0px;
}

.canvas-container {
    overflow: hidden;
    background: white;
}

.font-puppies-play {
    font-family: 'Puppies Play', cursive;
}

.font-aboreto {
    font-family: 'Aboreto', cursive;
}

.font-dancing-script {
    font-family: 'Dancing Script', cursive;
}

/* Error state */
.pi-error {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8fafc;
    padding: 24px;
}
.pi-error__card {
    max-width: 440px;
    width: 100%;
    text-align: center;
    background: #fff;
    border-radius: 16px;
    padding: 48px 32px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.06);
}
.pi-error__icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #fef2f2;
    color: #dc2626;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    margin: 0 auto 20px;
}
.pi-error__title {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 12px;
}
.pi-error__desc {
    font-size: 14px;
    color: #64748b;
    line-height: 1.6;
    margin: 0 0 28px;
}
.pi-error__btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    border-radius: 10px;
    background: #0f172a;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    transition: background 0.2s;
}
.pi-error__btn:hover { background: #1e293b; }

/* Location cards – public view */
.loc-cards-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 2.5rem;
    padding: 0 8px;
    max-width: 900px;
    margin-left: auto;
    margin-right: auto;
}
@media (min-width: 768px) {
    .loc-cards-grid {
        grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
        gap: 20px;
        padding: 0;
    }
}
.loc-card-public {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.10);
    transition: transform 0.2s, box-shadow 0.2s;
}
.loc-card-public:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.14);
}
.loc-card-public__figure {
    margin: 0;
    overflow: hidden;
    max-height: 180px;
}
.loc-card-public__img {
    width: 100%;
    height: 180px;
    object-fit: cover;
    display: block;
}
.loc-card-public__body {
    padding: 16px 18px 20px;
    text-align: left;
}
.loc-card-public__title {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 10px;
}
.loc-card-public__detail {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #334155;
    margin-bottom: 6px;
    line-height: 1.4;
}
.loc-card-public__detail--muted {
    color: #64748b;
    font-size: 13px;
    padding-left: 24px;
}
.loc-card-public__icon {
    width: 14px;
    color: #64748b;
    flex-shrink: 0;
}
.loc-card-public__map-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    padding: 8px 16px;
    border-radius: 8px;
    background: #0f172a;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    transition: background 0.2s;
}
.loc-card-public__map-btn:hover {
    background: #1e293b;
    color: #fff;
}

/* Dynamic text blocks */
.fs-text-block {
    position: relative;
}
.fs-add-text-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 16px;
    padding: 8px 18px;
    border: 2px dashed rgba(0,0,0,0.25);
    border-radius: 10px;
    background: rgba(0,0,0,0.06);
    backdrop-filter: blur(4px);
    color: rgba(0,0,0,0.5);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
}
.fs-add-text-btn:hover {
    border-color: rgba(0,0,0,0.4);
    background: rgba(0,0,0,0.1);
    color: rgba(0,0,0,0.7);
}

/* Loading overlay */
.pi-loader {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
}
.pi-loader__dots {
    display: flex;
    gap: 8px;
}
.pi-loader__dots span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #c4a77d;
    animation: pi-dot-pulse 1.2s ease-in-out infinite;
}
.pi-loader__dots span:nth-child(2) {
    animation-delay: 0.15s;
}
.pi-loader__dots span:nth-child(3) {
    animation-delay: 0.3s;
}
@keyframes pi-dot-pulse {
    0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
    40% { opacity: 1; transform: scale(1.2); }
}

/* Loading overlay fade-out */
.pi-loader-fade-leave-active {
    transition: opacity 0.4s ease;
}
.pi-loader-fade-leave-to {
    opacity: 0;
}
</style>
